import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Modal from '@mui/material/Modal';
import Rails from '@rails/ujs';
import Axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import toastr from 'toastr';
import {
  Nullable,
  ProjectTaskData,
  ProjectTaskDetailsData,
  TaskAttachmentData,
  TaskCommentData,
} from '../Common/types';

interface TaskDetailsModalProps {
  open: boolean;
  projectTask: ProjectTaskData;
  onClose: () => void;
  onCountsChange: (comments: number, attachments: number) => void;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 900,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto' as 'auto',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const headers = () => ({
  accept: 'application/json',
  'X-CSRF-Token': Rails.csrfToken(),
});

function formatSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function errorMessage(err: any): string {
  const data = err.response && err.response.data;
  if (data && data.errors) return data.errors.join(', ');
  if (data && data.error) return data.error;
  return err.message;
}

const InfoLine = (props: { label: string; children: React.ReactNode }) => (
  <div className="col-sm-6 m-b-xs">
    <strong>{props.label}:</strong> {props.children || '-'}
  </div>
);

const TaskDetailsModal = (props: TaskDetailsModalProps) => {
  const [details, setDetails] = useState<Nullable<ProjectTaskDetailsData>>(null),
    [comments, setComments] = useState<TaskCommentData[]>([]),
    [attachments, setAttachments] = useState<TaskAttachmentData[]>([]),
    [newComment, setNewComment] = useState(''),
    [savingComment, setSavingComment] = useState(false),
    [uploading, setUploading] = useState(false),
    fileInput = useRef<HTMLInputElement>(null);

  const taskUrl = `/task_management/${props.projectTask.id}`;
  const task = details || props.projectTask;

  function loadDetails() {
    Axios({ url: `${taskUrl}.json` })
      .then((res) => {
        setDetails(res.data);
        setComments(res.data.comments);
        setAttachments(res.data.attachments);
      })
      .catch((err) => toastr.error(errorMessage(err), 'Task details not loaded'));
  }

  function addComment() {
    if (newComment.trim() === '') return;

    setSavingComment(true);
    Axios.post(
      `${taskUrl}/comments.json`,
      { task_comment: { body: newComment } },
      { headers: headers() }
    )
      .then((res) => {
        setComments([...comments, res.data]);
        setNewComment('');
      })
      .catch((err) => toastr.error(errorMessage(err), 'Comment not added'))
      .finally(() => setSavingComment(false));
  }

  function removeComment(comment: TaskCommentData) {
    if (!window.confirm('Remove this comment?')) return;

    Axios.delete(`${taskUrl}/comments/${comment.id}.json`, { headers: headers() })
      .then(() => setComments(comments.filter((c) => c.id !== comment.id)))
      .catch((err) => toastr.error(errorMessage(err), 'Comment not removed'));
  }

  function uploadFiles(files: FileList | null) {
    if (!files || files.length === 0) return;

    const data = new FormData();
    Array.from(files).forEach((f) => data.append('files[]', f));

    setUploading(true);
    Axios.post(`${taskUrl}/attachments.json`, data, { headers: headers() })
      .then((res) => {
        setAttachments([...attachments, ...res.data.task_attachments]);
        if (res.data.errors.length > 0) {
          toastr.warning(res.data.errors.join(', '), 'Some files not uploaded');
        }
      })
      .catch((err) => toastr.error(errorMessage(err), 'Files not uploaded'))
      .finally(() => {
        setUploading(false);
        if (fileInput.current) fileInput.current.value = '';
      });
  }

  function removeAttachment(attachment: TaskAttachmentData) {
    if (!window.confirm(`Remove file ${attachment.filename}?`)) return;

    Axios.delete(`${taskUrl}/attachments/${attachment.id}.json`, { headers: headers() })
      .then(() => setAttachments(attachments.filter((a) => a.id !== attachment.id)))
      .catch((err) => toastr.error(errorMessage(err), 'File not removed'));
  }

  useEffect(() => {
    if (props.open) loadDetails();
  }, [props.open]);

  useEffect(() => {
    if (details) props.onCountsChange(comments.length, attachments.length);
  }, [comments, attachments]);

  return (
    <Modal open={props.open} onClose={props.onClose} aria-labelledby="task-details-title">
      <Box sx={style}>
        <Stack spacing={2} direction="column">
          <Typography id="task-details-title" variant="h6" component="h2">
            {task.task_type ? task.task_type.name : ''}
            {details && details.task_type_label && ` (${details.task_type_label})`}
          </Typography>

          <div className="row">
            <InfoLine label="Task ID">{task.id}</InfoLine>
            <InfoLine label="Order ID">{task.project && task.project.order_visible_id}</InfoLine>
            <InfoLine label="Order name">{task.project && task.project.name}</InfoLine>
          </div>

          <Divider />

          <Typography variant="subtitle1">
            <i className="fa fa-comments-o"></i> Comments ({comments.length})
          </Typography>
          {!details && <CircularProgress size={24} />}
          {details && comments.length === 0 && <small className="text-muted">No comments yet</small>}
          {comments.map((c) => (
            <Stack key={c.id} direction="row" justifyContent="space-between" alignItems="flex-start">
              <div>
                <div className="text-muted" style={{ fontSize: 14 }}>
                  {c.user_name} · {c.created_at}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 18 }}>{c.body}</div>
              </div>
              {c.removable && (
                <IconButton size="small" title="Remove comment" onClick={() => removeComment(c)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
          <TextField
            multiline
            minRows={3}
            placeholder="Write a comment..."
            inputProps={{ style: { fontSize: 18 } }}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addComment();
            }}
          />
          <Stack direction="row" justifyContent="end">
            <Button
              variant="contained"
              disabled={savingComment || newComment.trim() === ''}
              onClick={addComment}
            >
              Add comment
            </Button>
          </Stack>

          <Divider />

          <Typography variant="subtitle1">
            <i className="fa fa-paperclip"></i> Files ({attachments.length})
          </Typography>
          {details && attachments.length === 0 && <small className="text-muted">No files yet</small>}
          {attachments.map((a) => (
            <Stack key={a.id} direction="row" justifyContent="space-between" alignItems="center">
              <div>
                <a href={a.url} target="_blank" rel="noopener noreferrer">
                  <i className="fa fa-file-o"></i> {a.filename}
                </a>{' '}
                <small className="text-muted">
                  {formatSize(a.file_size)} · {a.user_name} · {a.created_at}
                </small>
              </div>
              {a.removable && (
                <IconButton size="small" title="Remove file" onClick={() => removeAttachment(a)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
          <Stack direction="row" spacing={2} alignItems="center">
            <input
              ref={fileInput}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => uploadFiles(e.target.files)}
            />
            <Button
              variant="outlined"
              disabled={uploading || !details}
              onClick={() => fileInput.current && fileInput.current.click()}
            >
              Upload files
            </Button>
            {uploading && <CircularProgress size={24} />}
          </Stack>

          <Divider />

          <Stack direction="row" justifyContent="end">
            <Button onClick={props.onClose}>Close</Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default TaskDetailsModal;
